/**
 * LegacyRedirectGuard — audit remediation Phase 2.
 *
 * The FoodPulse SEO audit flagged that legacy Ghost URLs
 * (`/food-donors`, `/food-wholesalers-distibutors`, `/grains-four`,
 * `/perishable-foods`, `/nature`, etc.) still resolve on foodpulse.net
 * and get indexed alongside the canonical new-app routes. This guard
 * fetches the merged legacy-redirect map from
 * `GET /api/legacy-redirects` and hard-redirects any matching pathname
 * to its canonical destination.
 *
 * Follow-up: we'll move the same map into an nginx/edge rewrite so
 * crawlers that don't execute JS see a real 301. For now this covers
 * Googlebot (which does render JS) and normal browser navigation.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { http } from "@/lib/api";

export default function LegacyRedirectGuard() {
  const { pathname, search, hash } = useLocation();
  const [redirects, setRedirects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    http.get("/legacy-redirects").then((r) => {
      if (!cancelled) setRedirects(r.data?.map || {});
    }).catch(() => setRedirects({}));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!redirects) return;
    const target = redirects[pathname];
    if (!target) return;
    // Absolute URL → hard nav; relative → client nav via location.
    if (/^https?:\/\//i.test(target)) {
      window.location.replace(target + hash);
    } else {
      window.location.replace(target + search + hash);
    }
  }, [redirects, pathname, search, hash]);

  return null;
}
